import React, { useState, useEffect } from 'react';
// nodejs library that concatenates classes
import classNames from 'classnames';
// @material-ui/core components
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useLocation, useNavigate } from 'react-router-dom';
import { notifyCartItemAdded } from '../../utils/cartUtils';

// @material-ui/icons
// core components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';

import styles from '../../assets/jss/material-kit-react/views/profilePage';
// Sections for this page
import NavPills from '../../components/NavPills/NavPills';
import CustomInput from '../../components/CustomInput/CustomInput';
import InputAdornment from '@mui/material/InputAdornment';
import Search from '@mui/icons-material/Search';
import config from '../../config/config.json';
import axios from 'axios';
import toast from 'react-hot-toast';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '../../components/CustomButtons/Button';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Close from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import blueIcon from '../../assets/img/blue_icon.webp';
import yellowIcon from '../../assets/img/yellow_icon.png';
import { YoutubeSearchedForOutlined } from '@mui/icons-material';
import { primaryColor } from '../../assets/jss/material-kit-react';
import { refreshCartBubble } from '../../utils/cartUtils';

const useStyles = makeStyles((theme) => ({
  ...styles,
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
  },
  card: {
    marginTop: '30px',
    marginBottom: '30px',
    width: '100%',
  },
  cardHeader: {
    width: 'auto',
    textAlign: 'center',
    marginLeft: '20px',
    marginRight: '20px',
    marginTop: '-40px',
    padding: '20px 0',
    marginBottom: '15px',
  },
  // ... existing styles ...
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export default function PublicSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useDocumentTitle(t('navigation.publicPage'));
  const classes = useStyles();
  const sessionToken = localStorage.getItem('session_token');
  const [minVal, setMinVal] = useState(1);
  const [maxVal, setMaxVal] = useState(999);
  const [isTyping, setIsTyping] = useState(false);
  const [dynamicPickitEntries, setDynamicPickitEntries] = useState([]);

  //get saved searches from this user and add them to suggestions
  const [baseSuggestions] = useState([
    {
      title: t('search.suggestions.paladinSkills', 'Paladin Skills'),
      search_term: ',paladin skill or paladin only',
    },
    {
      title: t('search.suggestions.sorceressSkills', 'Sorceress Skills'),
      search_term: ',sorceress Skill or sorceress only',
    },
    {
      title: t('search.suggestions.necromancerSkills', 'Necromancer Skills'),
      search_term: ',necromancer Skill or necromancer only',
    },
    {
      title: t('search.suggestions.assassinSkills', 'Assassin Skills'),
      search_term: ',assassin Skill or assassin only',
    },
    {
      title: t('search.suggestions.fasterCastRate', 'faster cast rate'),
      search_term: ',faster cast=1-20',
    },
    { title: t('search.suggestions.life', 'life'), search_term: ',life=1-999' },
    {
      title: t('search.suggestions.strengthDexterity', 'Strength or Dexterity'),
      search_term: ',str=1-99 or dex=1-99',
    },
    { title: t('search.suggestions.allResist', 'all resist'), search_term: ',all resist' },
    { title: t('search.suggestions.fireResist', 'fire resist'), search_term: ',fire resist' },
    { title: t('search.suggestions.coldResist', 'cold resist'), search_term: ',cold resist' },
    {
      title: t('search.suggestions.lightningResist', 'lightning resist'),
      search_term: ',lightning resist',
    },
    { title: t('search.suggestions.lifeLeech', 'Life Leech'), search_term: ',life stolen' },
    { title: t('search.suggestions.manaLeech', 'Mana Leech'), search_term: ',mana stolen' },
    { title: t('search.suggestions.maxDamage', 'Maximum Damage'), search_term: ',max' },
    { title: t('search.suggestions.attackRating', 'Attack Rating'), search_term: ',attack rat' },
    {
      title: t('search.suggestions.increasedAttackSpeed', 'Increased Attack Speed'),
      search_term: ',increased attack',
    },
    { title: t('search.suggestions.minSockets', 'Minimum 2 Sockets'), search_term: ',socketed=2' },
    { title: t('search.suggestions.unidentified', 'Unidentified'), search_term: ',unid' },
    { title: t('search.suggestions.ethereal', 'Ethereal'), search_term: ',eth' },
    {
      title: t('search.suggestions.replenishRepair', 'Replenishes/Repairs'),
      search_term: ',replenishes or repairs',
    },
    { title: t('search.suggestions.lld9', 'LLD 9'), search_term: ',required level=1-9' },
    { title: t('search.suggestions.lld30', 'LLD 30'), search_term: ',required level=1-30' },
    { title: t('search.suggestions.mld49', 'MLD 49'), search_term: ',required level=1-49' },
  ]);
  const [baseRarities] = useState([
    { title: t('search.rarities.rareCraft', 'Rare/Craft'), search_term: ',rare' },
    { title: t('search.rarities.magic', 'Magic'), search_term: ',magic' },
    { title: t('search.rarities.unique', 'Unique'), search_term: ',unique' },
    { title: t('search.rarities.set', 'Set'), search_term: ',set' },
    { title: t('search.rarities.whiteBases', 'White/Bases'), search_term: ',white' },
    { title: t('search.rarities.runeword', 'Runeword'), search_term: ',runeword' },
    { title: t('search.rarities.rune', 'Rune'), search_term: ',rune' },
  ]);
  const location = useLocation();

  // Extract query parameter from URL
  const queryParams = new URLSearchParams(window.location.search);
  const qterm = queryParams.get('term') || ''; // Default to empty string if 'term' is not present

  // Use the extracted value as the default for baseSearchTerm
  const [baseSearchTerm, setBaseSearchTerm] = useState(qterm);
  const [searchTerm, setSearchTerm] = useState(baseSearchTerm);
  const [oldCheck, setOldCheck] = useState('');

  // Handle URL query parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const qterm = queryParams.get('term') || '';
    setBaseSearchTerm(qterm);
    setSearchTerm(qterm);
    setOldCheck(qterm);
  }, [location.search]);

  // Handle search term changes
  useEffect(() => {
    if (oldCheck !== searchTerm) {
      setOldCheck(searchTerm);
      if (!isTyping) {
        getItemsSearch();
      }
    }
  }, [searchTerm, oldCheck, isTyping]);

  let sss = localStorage.getItem('showSavedSearches');
  if (!sss) {
    sss = 0;
  }
  let sr = localStorage.getItem('showRarities');
  if (!sr) {
    sr = 0;
  }
  let sc = localStorage.getItem('showCategories');
  if (!sc) {
    sc = 0;
  }
  let ss = localStorage.getItem('showStats');
  if (!ss) {
    ss = 0;
  }

  const [showSavedSearches, setShowSavedSearches] = useState(parseInt(sss));
  const [showRarities, setShowRarities] = useState(parseInt(sr));
  const [showCategories, setShowCategories] = useState(parseInt(sc));
  const [showStats, setShowStats] = useState(parseInt(ss));
  const [itemData, setItemData] = useState([]);
  const [statFilters, setStatFilters] = useState([]);
  const [searchTermFilters, setSearchTermFilters] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [detailModal, setDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const [activeTab, setActiveTab] = useState('items');
  const [currentItemPage, setCurrentItemPage] = useState(1);
  const [currentProductPage, setCurrentProductPage] = useState(1);
  const itemsPerPage = 20;

  const combinedItems = [
    ...itemData,
    ...dynamicPickitEntries.map((entry) => ({
      id: entry.id,
      name: entry.title || entry.name,
      context: 'all',
      image_path: 'https://d2r-member.com/image/' + (entry.product_image_path || ''),
      skin_image_path: 'https://d2r-member.com/skin/' + (entry.skin_image_path || ''),
      d2rshopproductlink: entry.shop_product_link || '',
      fiat_price:
        entry.fg_price !== '???'
          ? ((parseFloat(entry.fg_price) / 166) * (entry.shop_price_factor || 1)).toFixed(2)
          : '???',
    })),
  ];

  const PaginationControls = ({ data, currentPage, setCurrentPage }) => {
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

    return totalPages > 1 ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
        <Button
          color="primary"
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
        >
          {t('common.previous')}
        </Button>
        <Box sx={{ mx: 2, display: 'flex', alignItems: 'center' }}>
          {t('common.page')} {currentPage} / {totalPages}
        </Box>
        <Button
          color="primary"
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
        >
          {t('common.next')}
        </Button>
      </Box>
    ) : null;
  };

  async function getPSSStats() {
    await axios
      .get(config.backend_base_url + 'pss/stats/all', {
        headers: { 'session-token': sessionToken },
      })
      .then((response) => {
        if (response.data != null) {
          setStatFilters(response.data);
        }
      })
      .catch((error) => {
        console.log('Error ========>', error);
        toast.error(
          t('search.errors.statsLoadFailed', 'Failed to load item stats. Please refresh the page')
        );
      });
  }

  function priceOffer(event, itemID) {
    event.preventDefault();

    const amount = window.prompt(t('search.offers.enterAmount'));
    if (!amount) {
      return;
    }

    const numericAmount = parseInt(amount, 10);
    if (isNaN(numericAmount) || numericAmount <= 0) {
      toast.error(t('search.errors.invalidOffer'));
      return;
    }

    axios
      .post(
        config.backend_base_url + 'pss/offer',
        {
          item_id: parseInt(itemID, 10),
          offer: numericAmount.toString(),
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((_) => {
        toast.success(t('search.item.offerCreated'));
      })
      .catch((error) => {
        console.log('Error ========>', error);
        toast.error(
          t('search.errors.createOfferFailed', 'Failed to create offer. Please try again')
        );
      });
  }

  function clickItemDetails(event, item, isProduct = false) {
    if (isProduct) {
      item.context = 'all';
    }
    event.preventDefault();
    setSelectedItem(item);
    setDetailModal(true);
  }

  function cartItem(event, itemID) {
    event.preventDefault();
    if (isNaN(itemID)) {
      toast.error(t('search.errors.invalidId', 'Invalid item ID'));
      return;
    }
    const loading = toast.loading(t('search.cart.adding', 'Adding item to cart'));

    // First find the target item (might be in itemData or dynamicPickitEntries)
    let targetItem = itemData.find((item) => Number(item.id) === Number(itemID));
    if (!targetItem) {
      targetItem = dynamicPickitEntries.find((item) => Number(item.id) === Number(itemID));
    }

    if (!targetItem) {
      toast.dismiss(loading);
      toast.error(t('search.errors.itemNotFound', 'Item not found'));
      return;
    }

    // Search for available items with the same attributes
    axios
      .post(
        `${config.backend_base_url}public/pss/search`,
        {
          search_term: targetItem.name || '',
          limit: 100, // Get enough items to find matches
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((searchResponse) => {
        if (!searchResponse.data || !Array.isArray(searchResponse.data)) {
          toast.dismiss(loading);
          toast.error(t('search.errors.searchFailed', 'Failed to find similar items'));
          return;
        }

        // Create a comprehensive key from item attributes to find similar items
        const targetKey = [
          targetItem.stats_description || '',
          targetItem.ocr_string || '',
          targetItem.rarity_id || '',
          targetItem.ladder || '',
          targetItem.hardcore || '',
          targetItem.skin_image_path || '', // Using skin_image_path as skin_name
          targetItem.fiat_price || '',
          targetItem.fg_price || '',
          targetItem.type_id || '',
        ].join('|');

        // Find available items with matching attributes
        const matchingItems = searchResponse.data.filter((item) => {
          if (item.status !== 'available') return false;

          const itemKey = [
            item.stats_description || '',
            item.ocr_string || '',
            item.rarity_id || '',
            item.ladder || '',
            item.hardcore || '',
            item.skin_image_path || '', // Using skin_image_path as skin_name
            item.fiat_price || '',
            item.fg_price || '',
            item.type_id || '',
          ].join('|');

          return itemKey === targetKey;
        });

        if (matchingItems.length === 0) {
          toast.dismiss(loading);
          toast.error(t('search.errors.noMoreItems', 'No more items available'));
          return;
        }

        // Add the first matching item to cart
        const itemToAdd = matchingItems[0];

        axios
          .post(
            `${config.backend_base_url}d2rs/cart/add`,
            { item_ids: [itemToAdd.id] },
            { headers: { 'session-token': sessionToken } }
          )
          .then((response) => {
            if (response.status) {
              toast.dismiss(loading);
              toast.success(t('search.cart.success', 'Successfully added item to cart'));

              // Notify components about cart update
              queryClient.invalidateQueries({ queryKey: ['cartItems'] });
              notifyCartItemAdded({ id: itemToAdd.id });
            }
          })
          .catch((error) => {
            console.log('Error ========>', error);
            toast.dismiss(loading);
            toast.error(t('search.errors.cartAddFailed', 'Failed to add item to cart'));
          });
      })
      .catch((error) => {
        console.log('Error ========>', error);
        toast.dismiss(loading);
        toast.error(t('search.errors.searchFailed', 'Failed to find similar items'));
      });
  }

  async function getPSSSearchTerms() {
    let url = config.backend_base_url + 'pss/terms/all';
    await axios
      .get(url, { headers: { 'session-token': sessionToken } })
      .then((response) => {
        if (response.data != null && response.data.length > 0) {
          setSearchTermFilters(response.data);
        } else {
          setSearchTermFilters([]);
        }
      })
      .catch((error) => {
        console.log('Error ========>', error);
        toast.error(
          t(
            'search.errors.searchTermsFailed',
            'Failed to load search terms. Please refresh the page'
          )
        );
      });
  }

  async function getItemsSearch() {
    const fetchingNotification = toast.loading(t('loading.fetchingData'));
    setIsTyping(false);
    let url = config.backend_base_url + 'public/pss/search';
    axios
      .post(
        url,
        {
          search_term: searchTerm,
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((response) => {
        if (response.data != null && response.data.length > 0) {
          toast.success(
            t('search.success.results', 'Search successful [{{count}}]', {
              count: response.data.length,
            })
          );
          setItemData(response.data);
          toast.dismiss(fetchingNotification);
        } else {
          toast.error(t('search.errors.noMatches', 'Search found no matches'));
          setItemData([]);
          toast.dismiss(fetchingNotification);
        }
      })
      .catch((error) => {
        console.log('Error ========>', error);
        toast.dismiss(fetchingNotification);
        toast.error(t('search.errors.searchFailed', 'Failed to search items. Please try again'));
      });
    const params = new URLSearchParams(location.search);
    if (searchTerm !== '') {
      params.set('term', searchTerm);
      const newUrl = `${location.pathname}?${params.toString()}`;
      window.history.pushState({}, '', newUrl);
    }
    // Also search for products
    getDynamicPickitEntries();
  }

  function getItemsSearchByEnter(event) {
    if (!isTyping) {
      setIsTyping(true);
    }
    if (event.key === 'Enter') {
      getItemsSearch();
    }
  }

  async function getDynamicPickitEntries() {
    let count = 0;
    let url = config.backend_base_url + 'ml/pickit/dynamic/psearch';
    await axios
      .post(
        url,
        {
          search_term: searchTerm,
        },
        { headers: { 'session-token': sessionToken } }
      )
      .then((response) => {
        if (response.data != null) {
          setDynamicPickitEntries(response.data);
          count = response.data.length;
        } else {
          setDynamicPickitEntries([]);
          count = 0;
        }
      })
      .catch((error) => {
        setDynamicPickitEntries([]);
        console.log('Error ========>', error);
        toast.error(t('errors.refreshPage'));
      });
    return count;
  }

  function onStatChange(event, values) {
    event.preventDefault();
    if (!values) {
      return;
    }
    let newTerm = searchTerm + ',' + values.label;
    if (values.label !== 'unid' && values.label !== 'eth') {
      newTerm += '=' + minVal + '-' + maxVal;
    }
    setSearchTerm(newTerm.replaceAll(',,', ','));
  }

  let handleSSToggle = function (event) {
    if (event.target.checked === true) {
      setShowSavedSearches(1);
      localStorage.setItem('showSavedSearches', 1);
    } else {
      setShowSavedSearches(0);
      localStorage.setItem('showSavedSearches', 0);
    }
  };
  let handleShowRarityToggle = function (event) {
    if (event.target.checked === true) {
      setShowRarities(1);
      localStorage.setItem('showRarities', 1);
    } else {
      setShowRarities(0);
      localStorage.setItem('showRarities', 0);
    }
  };
  let handleShowCategories = function (event) {
    if (event.target.checked === true) {
      setShowCategories(1);
      localStorage.setItem('showCategories', 1);
    } else {
      setShowCategories(0);
      localStorage.setItem('showCategories', 0);
    }
  };
  let handleShowStatsToggle = function (event) {
    if (event.target.checked === true) {
      setShowStats(1);
      localStorage.setItem('showStats', 1);
    } else {
      setShowStats(0);
      localStorage.setItem('showStats', 0);
    }
  };

  function resetTermFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('term');
    const newUrl = `${location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  }
  function clearSearch(event) {
    event.preventDefault();
    resetTermFromUrlParams();
    setItemData([]);
    setDynamicPickitEntries([]);
    setSearchTerm('');
    toast.success(t('search.filters.cleared', 'Search cleared'));
  }

  function setNewRarTerm(event, newRar) {
    event.preventDefault();
    let newTerm = searchTerm + ',' + newRar;
    let arr = searchTerm.split(',');

    if (searchTerm.includes(newRar)) {
      newTerm = searchTerm.replaceAll(newRar, '');
    } else {
      if (searchTerm.includes(',')) {
        let filtered = arr.filter(withoutRarTerm);
        newTerm = filtered.join(',') + newRar;
      }
    }

    setSearchTerm(newTerm.replaceAll(',,', ','));
  }

  function withoutRarTerm(term) {
    let bools = baseRarities.some((value) => {
      let comp1 = value.search_term.replaceAll(',', '');
      let comp2 = term.replaceAll(',', '');
      return comp1 === comp2;
    });
    return !bools;
  }

  function setNewCategory(event, newCat) {
    event.preventDefault();
    let newTerm = newCat;
    let arr = searchTerm.split(',');
    if (newCat === searchTerm.split(',')[0]) {
      //we need to deselect this
      arr.shift();
      newTerm = arr.join(',');
    } else {
      //we select this
      if (searchTerm.includes(',')) {
        let filtered = arr.filter(withoutCatTerm);
        if (filtered.length > 0) {
          newTerm += ',' + filtered.join(',');
        }
      }
    }

    setSearchTerm(newTerm.replaceAll(',,', ','));
  }

  function withoutCatTerm(term) {
    return !searchTermFilters.some((value) => {
      return value.label === term;
    });
  }

  function goToSavedSearchesToolPage(event) {
    event.preventDefault();
    navigate('/saved-searches');
  }

  function handleSuggestionChange(newVal) {
    let newTerm = searchTerm;
    if (searchTerm.includes(newVal)) {
      newTerm = searchTerm.replaceAll(newVal, '');
    } else {
      newTerm = searchTerm + newVal;
    }
    setSearchTerm(newTerm.replaceAll(',,', ''));
  }

  function setMaxValFunction(newVal) {
    if (isNaN(newVal) || !newVal) {
      return;
    }
    setMaxVal(newVal);
  }

  function setMinValFunction(newVal) {
    if (isNaN(newVal) || !newVal) {
      return;
    }
    setMinVal(newVal);
  }

  async function getSavedSearches() {
    let url = config.backend_base_url + 'ml/search/mine';
    if (!sessionToken) {
      return;
    }
    await axios
      .get(url, { headers: { 'session-token': sessionToken } })
      .then((response) => {
        if (response.data != null && response.data.length > 0) {
          setSavedSearches(response.data);
        } else {
          setSavedSearches([]);
        }
      })
      .catch((error) => {
        console.log('Error ========>', error);
        toast.error(t('search.errors.savedSearchesFailed', 'Failed to load saved searches'));
      });
  }

  const handleChange = () => {};

  if (loadingData) {
    getItemsSearch();
    getPSSStats();
    getPSSSearchTerms();
    getSavedSearches();
    setLoadingData(false);
  }

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card className={classes.card}>
              <CardHeader color="primary" className={classes.cardHeader}>
                <h4>{t('navigation.publicPage')}</h4>
              </CardHeader>
              <CardBody>
                <NavPills
                  queryKey={'t'}
                  alignCenter
                  color="primary"
                  onChange={(tab) => setActiveTab(tab)}
                  tabs={[
                    {
                      tabName: 'items',
                      tabButton: (
                        <span style={{ display: 'block' }}>
                          {t('search.tabs.items')} ({itemData.length}){' '}
                          <img
                            style={{ width: '16px' }}
                            src={blueIcon}
                            alt={t('search.tabs.items')}
                          />
                        </span>
                      ),
                      tabContent: (
                        <span>
                          <GridContainer>
                            <GridItem md={9}>
                              <CustomInput
                                labelText={t('search.input.label')}
                                id="searchTerm"
                                value={searchTerm}
                                formControlProps={{
                                  fullWidth: true,
                                }}
                                onKeyDown={getItemsSearchByEnter}
                                onChange={(params) => {
                                  setSearchTerm(params.target.value);
                                }}
                                inputProps={{
                                  type: 'text',
                                  placeholder: t('search.input.placeholder'),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Search className={classes.inputIconsColor} />
                                    </InputAdornment>
                                  ),
                                  autoComplete: 'off',
                                }}
                              />
                            </GridItem>
                            <GridItem md={1.5}>
                              <Button color="primary" size="sm" onClick={getItemsSearch} fullWidth>
                                {t('search.buttons.search')} ({itemData.length})
                              </Button>
                            </GridItem>
                            <GridItem md={1.5}>
                              <Button color="warning" size="sm" onClick={clearSearch} fullWidth>
                                {t('search.buttons.clear')}
                              </Button>
                            </GridItem>
                          </GridContainer>
                          <GridItem xs={12} sm={12}></GridItem>

                          <GridContainer>
                            {sessionToken ? (
                              <GridItem xs={12} sm={4} md={3}>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={showSavedSearches === 1}
                                        id={'sstoggle'}
                                        onChange={handleSSToggle}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                      />
                                    }
                                    label={
                                      <span style={{ display: 'flex', alignItems: 'center' }}>
                                        {t('search.filters.showSavedSearches')}
                                        <YoutubeSearchedForOutlined
                                          style={{ marginRight: '8px' }}
                                        />
                                      </span>
                                    }
                                    style={{ marginTop: '7px' }}
                                  />
                                </FormGroup>
                              </GridItem>
                            ) : (
                              <h1></h1>
                            )}
                            <GridItem xs={12} sm={4} md={3}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={showRarities === 1}
                                      id={'rartoggle'}
                                      onChange={handleShowRarityToggle}
                                      inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                  }
                                  label={t('search.filters.showRarity')}
                                  style={{ marginTop: '7px' }}
                                />
                              </FormGroup>
                            </GridItem>
                            <GridItem xs={12} sm={4} md={3}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={showCategories === 1}
                                      id={'cattoggle'}
                                      onChange={handleShowCategories}
                                      inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                  }
                                  label={t('search.filters.showCategories')}
                                  style={{ marginTop: '7px' }}
                                />
                              </FormGroup>
                            </GridItem>
                            <GridItem xs={12} sm={4} md={3}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={showStats === 1}
                                      id={'stattoggle'}
                                      onChange={handleShowStatsToggle}
                                      inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                  }
                                  label={t('search.filters.showStats')}
                                  style={{ marginTop: '7px' }}
                                />
                              </FormGroup>
                            </GridItem>

                            <GridItem xs={12} sm={12} md={12}>
                              {sessionToken && showSavedSearches === 1 ? (
                                <div>
                                  {savedSearches.map((st, key) => (
                                    <Button
                                      color={primaryColor}
                                      key={key}
                                      size="sm"
                                      onClick={() => setSearchTerm(st.search_term)}
                                    >
                                      {st.search_name} ({st.search_count})
                                    </Button>
                                  ))}
                                  <Button
                                    color="outlined"
                                    size="sm"
                                    onClick={goToSavedSearchesToolPage}
                                  >
                                    {t('search.filters.createNewSavedSearches')}
                                  </Button>
                                </div>
                              ) : (
                                <div></div>
                              )}
                            </GridItem>
                            <GridItem xs={12} sm={12} md={12}>
                              {showRarities === 1 ? (
                                <div>
                                  {baseRarities.map((sugg, key) => (
                                    <Button
                                      color={
                                        searchTerm.includes(sugg.search_term) ? 'danger' : 'grey'
                                      }
                                      key={key}
                                      size="sm"
                                      onClick={() => setNewRarTerm(event, sugg.search_term)}
                                    >
                                      {searchTerm.includes(sugg.search_term) ? <Close></Close> : ''}
                                      {sugg.title}
                                    </Button>
                                  ))}
                                </div>
                              ) : (
                                <div></div>
                              )}
                            </GridItem>
                            <GridItem xs={12} sm={12} md={12}>
                              {showCategories === 1 ? (
                                <div>
                                  {searchTermFilters.map((st, key) => (
                                    <Button
                                      // color={st.label.includes((searchTerm.split(",")[0]) && searchTerm.includes(",")) || st.label === searchTerm ? ("danger"):("grey")}
                                      color={
                                        (st.label.includes(searchTerm) &&
                                          searchTerm !== '' &&
                                          !searchTerm.includes(',')) ||
                                        (st.label.includes(searchTerm.split(',')[0]) &&
                                          searchTerm.split(',')[0] !== '' &&
                                          searchTerm.includes(',')) ||
                                        st.label === searchTerm
                                          ? 'danger'
                                          : 'grey'
                                      }
                                      key={key}
                                      size="sm"
                                      onClick={() => setNewCategory(event, st.label)}
                                    >
                                      {st.label === searchTerm.split(',')[0] ? <Close></Close> : ''}
                                      {st.label}
                                    </Button>
                                  ))}
                                </div>
                              ) : (
                                <div></div>
                              )}
                            </GridItem>
                            <GridItem xs={12} sm={12} md={12}>
                              {showStats === 1 ? (
                                <GridContainer justify="center">
                                  <GridItem xs={6} sm={12} md={3}>
                                    <CustomInput
                                      labelText={t('search.filters.minValue')}
                                      id="minValue"
                                      value={minVal}
                                      formControlProps={{
                                        fullWidth: true,
                                      }}
                                      onChange={(params) => {
                                        setMinValFunction(params.target.value);
                                      }}
                                      inputProps={{
                                        type: 'number',
                                      }}
                                    />
                                  </GridItem>
                                  <GridItem xs={6} sm={6} md={3}>
                                    <CustomInput
                                      labelText={t('search.filters.maxValue')}
                                      id="maxValue"
                                      value={maxVal}
                                      formControlProps={{
                                        fullWidth: true,
                                      }}
                                      onChange={(params) => {
                                        setMaxValFunction(params.target.value);
                                      }}
                                      inputProps={{
                                        type: 'number',
                                      }}
                                    />
                                  </GridItem>
                                  <GridItem xs={12} sm={12} md={6} style={{ paddingTop: 10 }}>
                                    <Autocomplete
                                      id="Stat Filters"
                                      options={statFilters}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label={t('search.stats.searchPlaceholder')}
                                        />
                                      )}
                                      onChange={onStatChange}
                                    />
                                  </GridItem>
                                  <GridItem xs={12} sm={12} md={12}>
                                    {baseSuggestions.map((sugg, key) => (
                                      <Button
                                        color={
                                          searchTerm.includes(sugg.search_term) ? 'danger' : 'grey'
                                        }
                                        key={key}
                                        size="sm"
                                        // onClick={() =>
                                        //  setSearchTerm(searchTerm + sugg.search_term)}
                                        onClick={() => handleSuggestionChange(sugg.search_term)}
                                      >
                                        {searchTerm.includes(sugg.search_term) ? (
                                          <Close></Close>
                                        ) : (
                                          ''
                                        )}
                                        {sugg.title}
                                      </Button>
                                    ))}
                                  </GridItem>
                                </GridContainer>
                              ) : (
                                <h1></h1>
                              )}
                            </GridItem>
                          </GridContainer>
                          <div>
                            <PaginationControls
                              data={itemData}
                              currentPage={currentItemPage}
                              setCurrentPage={setCurrentItemPage}
                            />
                            <GridContainer justify="center" spacing={1}>
                              {itemData
                                .slice(
                                  (currentItemPage - 1) * itemsPerPage,
                                  currentItemPage * itemsPerPage
                                )
                                .map((item, key) => (
                                  <GridItem
                                    xs={12}
                                    sm={4}
                                    md={3}
                                    key={key}
                                    style={{ textAlign: 'center' }}
                                  >
                                    <GridContainer>
                                      <GridItem xs={12} sm={12} md={12}>
                                        <b>
                                          {item.name} [{item.context}]{' '}
                                          {item.d2rshopproductlink !== '' ? (
                                            <span>
                                              {' '}
                                              - {item.fiat_price?.replaceAll('.00', '')}$
                                            </span>
                                          ) : (
                                            <span></span>
                                          )}
                                        </b>
                                      </GridItem>
                                    </GridContainer>
                                    <br />

                                    <img
                                      onClick={() => clickItemDetails(event, item)}
                                      src={
                                        item.context !== 'all'
                                          ? item.d2rshopproductlink !== ''
                                            ? 'https://d2r-member.com/image/d2rs' + item.id + '.png'
                                            : 'https://d2r-member.com/image/' + item.image_path
                                          : item.image_path
                                      }
                                      alt={item.name}
                                      style={{
                                        display: 'block',
                                        marginLeft: 'auto',
                                        marginRight: 'auto',
                                        width: '100%',
                                        maxHeight: '400px',
                                        objectFit: 'contain',
                                        objectPosition: 'center',
                                      }}
                                    />
                                  </GridItem>
                                ))}
                            </GridContainer>
                            <PaginationControls
                              data={itemData}
                              currentPage={currentItemPage}
                              setCurrentPage={setCurrentItemPage}
                            />
                          </div>
                        </span>
                      ),
                    },
                    {
                      tabName: 'products',
                      tabButton: (
                        <span style={{ display: 'block' }}>
                          {t('search.tabs.products')} ({dynamicPickitEntries.length}){' '}
                          <img
                            style={{ width: '16px' }}
                            src={yellowIcon}
                            alt={t('search.tabs.products')}
                          />
                        </span>
                      ),
                      tabContent: (
                        <span>
                          <GridContainer>
                            <GridItem md={9}>
                              <CustomInput
                                labelText={t('search.input.label')}
                                id="searchTerm"
                                value={searchTerm}
                                formControlProps={{
                                  fullWidth: true,
                                }}
                                onKeyDown={getItemsSearchByEnter}
                                onChange={(params) => {
                                  setSearchTerm(params.target.value);
                                }}
                                inputProps={{
                                  type: 'text',
                                  placeholder: t('search.input.placeholder'),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Search className={classes.inputIconsColor} />
                                    </InputAdornment>
                                  ),
                                  autoComplete: 'off',
                                }}
                              />
                            </GridItem>
                            <GridItem md={1.5}>
                              <Button color="primary" size="sm" onClick={getItemsSearch} fullWidth>
                                {t('search.buttons.search')} ({dynamicPickitEntries.length})
                              </Button>
                            </GridItem>
                            <GridItem md={1.5}>
                              <Button color="warning" size="sm" onClick={clearSearch} fullWidth>
                                {t('search.buttons.clear')}
                              </Button>
                            </GridItem>
                          </GridContainer>
                          <GridItem xs={12} sm={12}></GridItem>

                          <GridContainer>
                            {sessionToken ? (
                              <GridItem xs={12} sm={4} md={3}>
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={showSavedSearches === 1}
                                        id={'sstoggle'}
                                        onChange={handleSSToggle}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                      />
                                    }
                                    label={
                                      <span style={{ display: 'flex', alignItems: 'center' }}>
                                        {t('search.filters.showSavedSearches')}
                                        <YoutubeSearchedForOutlined
                                          style={{ marginRight: '8px' }}
                                        />
                                      </span>
                                    }
                                    style={{ marginTop: '7px' }}
                                  />
                                </FormGroup>
                              </GridItem>
                            ) : (
                              <h1></h1>
                            )}
                            <GridItem xs={12} sm={4} md={3}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={showRarities === 1}
                                      id={'rartoggle'}
                                      onChange={handleShowRarityToggle}
                                      inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                  }
                                  label={t('search.filters.showRarity')}
                                  style={{ marginTop: '7px' }}
                                />
                              </FormGroup>
                            </GridItem>
                            <GridItem xs={12} sm={4} md={3}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={showCategories === 1}
                                      id={'cattoggle'}
                                      onChange={handleShowCategories}
                                      inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                  }
                                  label={t('search.filters.showCategories')}
                                  style={{ marginTop: '7px' }}
                                />
                              </FormGroup>
                            </GridItem>
                            <GridItem xs={12} sm={4} md={3}>
                              <FormGroup>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={showStats === 1}
                                      id={'stattoggle'}
                                      onChange={handleShowStatsToggle}
                                      inputProps={{ 'aria-label': 'controlled' }}
                                    />
                                  }
                                  label={t('search.filters.showStats')}
                                  style={{ marginTop: '7px' }}
                                />
                              </FormGroup>
                            </GridItem>

                            <GridItem xs={12} sm={12} md={12}>
                              {sessionToken && showSavedSearches === 1 ? (
                                <div>
                                  {savedSearches.map((st, key) => (
                                    <Button
                                      color={primaryColor}
                                      key={key}
                                      size="sm"
                                      onClick={() => setSearchTerm(st.search_term)}
                                    >
                                      {st.search_name} ({st.search_count})
                                    </Button>
                                  ))}
                                  <Button
                                    color="outlined"
                                    size="sm"
                                    onClick={goToSavedSearchesToolPage}
                                  >
                                    {t('search.filters.createNewSavedSearches')}
                                  </Button>
                                </div>
                              ) : (
                                <div></div>
                              )}
                            </GridItem>
                            <GridItem xs={12} sm={12} md={12}>
                              {showRarities === 1 ? (
                                <div>
                                  {baseRarities.map((sugg, key) => (
                                    <Button
                                      color={
                                        searchTerm.includes(sugg.search_term) ? 'danger' : 'grey'
                                      }
                                      key={key}
                                      size="sm"
                                      onClick={() => setNewRarTerm(event, sugg.search_term)}
                                    >
                                      {searchTerm.includes(sugg.search_term) ? <Close></Close> : ''}
                                      {sugg.title}
                                    </Button>
                                  ))}
                                </div>
                              ) : (
                                <div></div>
                              )}
                            </GridItem>
                            <GridItem xs={12} sm={12} md={12}>
                              {showCategories === 1 ? (
                                <div>
                                  {searchTermFilters.map((st, key) => (
                                    <Button
                                      // color={st.label.includes((searchTerm.split(",")[0]) && searchTerm.includes(",")) || st.label === searchTerm ? ("danger"):("grey")}
                                      color={
                                        (st.label.includes(searchTerm) &&
                                          searchTerm !== '' &&
                                          !searchTerm.includes(',')) ||
                                        (st.label.includes(searchTerm.split(',')[0]) &&
                                          searchTerm.split(',')[0] !== '' &&
                                          searchTerm.includes(',')) ||
                                        st.label === searchTerm
                                          ? 'danger'
                                          : 'grey'
                                      }
                                      key={key}
                                      size="sm"
                                      onClick={() => setNewCategory(event, st.label)}
                                    >
                                      {st.label === searchTerm.split(',')[0] ? <Close></Close> : ''}
                                      {st.label}
                                    </Button>
                                  ))}
                                </div>
                              ) : (
                                <div></div>
                              )}
                            </GridItem>
                            <GridItem xs={12} sm={12} md={12}>
                              {showStats === 1 ? (
                                <GridContainer justify="center">
                                  <GridItem xs={6} sm={12} md={3}>
                                    <CustomInput
                                      labelText={t('search.filters.minValue')}
                                      id="minValue"
                                      value={minVal}
                                      formControlProps={{
                                        fullWidth: true,
                                      }}
                                      onChange={(params) => {
                                        setMinValFunction(params.target.value);
                                      }}
                                      inputProps={{
                                        type: 'number',
                                      }}
                                    />
                                  </GridItem>
                                  <GridItem xs={6} sm={6} md={3}>
                                    <CustomInput
                                      labelText={t('search.filters.maxValue')}
                                      id="maxValue"
                                      value={maxVal}
                                      formControlProps={{
                                        fullWidth: true,
                                      }}
                                      onChange={(params) => {
                                        setMaxValFunction(params.target.value);
                                      }}
                                      inputProps={{
                                        type: 'number',
                                      }}
                                    />
                                  </GridItem>
                                  <GridItem xs={12} sm={12} md={6} style={{ paddingTop: 10 }}>
                                    <Autocomplete
                                      id="Stat Filters"
                                      options={statFilters}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label={t('search.stats.searchPlaceholder')}
                                        />
                                      )}
                                      onChange={onStatChange}
                                    />
                                  </GridItem>
                                  <GridItem xs={12} sm={12} md={12}>
                                    {baseSuggestions.map((sugg, key) => (
                                      <Button
                                        color={
                                          searchTerm.includes(sugg.search_term) ? 'danger' : 'grey'
                                        }
                                        key={key}
                                        size="sm"
                                        // onClick={() =>
                                        //  setSearchTerm(searchTerm + sugg.search_term)}
                                        onClick={() => handleSuggestionChange(sugg.search_term)}
                                      >
                                        {searchTerm.includes(sugg.search_term) ? (
                                          <Close></Close>
                                        ) : (
                                          ''
                                        )}
                                        {sugg.title}
                                      </Button>
                                    ))}
                                  </GridItem>
                                </GridContainer>
                              ) : (
                                <h1></h1>
                              )}
                            </GridItem>
                          </GridContainer>
                          <div>
                            <PaginationControls
                              data={dynamicPickitEntries}
                              currentPage={currentProductPage}
                              setCurrentPage={setCurrentProductPage}
                            />
                            <GridContainer justify="center" spacing={1}>
                              {dynamicPickitEntries
                                .slice(
                                  (currentProductPage - 1) * itemsPerPage,
                                  currentProductPage * itemsPerPage
                                )
                                .map((product, key) => (
                                  <GridItem
                                    xs={12}
                                    sm={4}
                                    md={3}
                                    key={key}
                                    style={{ textAlign: 'center' }}
                                  >
                                    <GridContainer>
                                      <GridItem xs={12} sm={12} md={12}>
                                        <h7>
                                          <b>
                                            {product.title || product.name} [all]{' '}
                                            {product.d2rshopproductlink !== '' ? (
                                              <span>
                                                {' '}
                                                -{' '}
                                                {product.fg_price !== '???'
                                                  ? (
                                                      (parseFloat(product.fg_price) / 166) *
                                                      (product.shop_price_factor || 1)
                                                    )
                                                      .toFixed(2)
                                                      .replaceAll('.00', '')
                                                  : '???'}
                                                $
                                              </span>
                                            ) : (
                                              <span></span>
                                            )}
                                          </b>
                                        </h7>
                                      </GridItem>
                                    </GridContainer>
                                    <br />

                                    <img
                                      onClick={() => clickItemDetails(event, product, true)}
                                      src={
                                        'https://d2r-member.com/image/' + product.product_image_path
                                      }
                                      alt={product.title || product.name}
                                      style={{
                                        display: 'block',
                                        marginLeft: 'auto',
                                        marginRight: 'auto',
                                        width: '100%',
                                        maxHeight: '400px',
                                        objectFit: 'contain',
                                        objectPosition: 'center',
                                      }}
                                    />
                                  </GridItem>
                                ))}
                            </GridContainer>
                            <PaginationControls
                              data={dynamicPickitEntries}
                              currentPage={currentProductPage}
                              setCurrentPage={setCurrentProductPage}
                            />
                          </div>
                        </span>
                      ),
                    },
                  ]}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      <Dialog
        disableEnforceFocus
        classes={{
          root: classes.center,
          paper: classes.modal,
        }}
        open={detailModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setDetailModal(false)}
        aria-labelledby="classic-modal-slide-title"
        aria-describedby="classic-modal-slide-description"
      >
        <DialogTitle id="classic-modal-slide-title" className={classes.modalHeader}>
          <IconButton
            className={classes.modalCloseButton}
            key="close"
            aria-label={t('search.dialog.close')}
            color="inherit"
            onClick={() => setDetailModal(false)}
          >
            <Close className={classes.modalClose} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          id="classic-modal-slide-description"
          className={classes.modalBody}
          style={{ textAlign: 'center' }}
        >
          <GridContainer justify="center" spacing={1}>
            <GridItem xs={12} sm={12} md={12}>
              <h3>
                {selectedItem.title || selectedItem.name} [{selectedItem.context}]
              </h3>
              <br />
              <h2>
                {selectedItem.d2rshopproductlink !== '' &&
                selectedItem.fiat_price !== '???' &&
                !isNaN(parseFloat(selectedItem.fiat_price)) ? (
                  <b>{parseFloat(selectedItem.fiat_price).toFixed(2).replaceAll('.00', '')}$</b>
                ) : (
                  <div></div>
                )}
              </h2>
            </GridItem>
            <GridItem xs={12} sm={12} md={12}>
              <Button
                color="primary"
                fullWidth={true}
                target="_blank"
                size="sm"
                onClick={() =>
                  window.open(
                    selectedItem.context === 'all'
                      ? `/p/${selectedItem.id}`
                      : `/i/${selectedItem.id}`,
                    '_blank'
                  )
                }
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <img
                  src={blueIcon}
                  alt="details"
                  style={{ width: '20px', height: '20px', marginRight: '8px' }}
                />
                {t('search.item.viewDetails', 'Details')}
              </Button>
            </GridItem>
            <GridItem xs={12} sm={12} md={12}>
              {selectedItem.d2rshopproductlink !== '' &&
                selectedItem.fiat_price !== '???' &&
                !isNaN(parseFloat(selectedItem.fiat_price)) && (
                  <Button
                    color="primary"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    href={selectedItem.d2rshopproductlink}
                    target="_blank"
                  >
                    <img
                      src={yellowIcon}
                      alt="shop"
                      style={{ width: '20px', height: '20px', marginRight: '8px' }}
                    />
                    {t('search.vendor.priceWithAmount', {
                      amount: selectedItem.fiat_price?.replaceAll('.00', '') || '',
                    })}
                  </Button>
                )}
              {selectedItem.context !== 'all' && (
                <Button
                  color="primary"
                  fullWidth={true}
                  size="sm"
                  onClick={() => priceOffer(event, selectedItem.id)}
                >
                  {t('search.offers.makeOfferAmount')}
                </Button>
              )}
            </GridItem>
            <GridItem xs={12} sm={12} md={12}>
              <img
                src={
                  selectedItem.context !== 'all'
                    ? selectedItem.d2rshopproductlink !== ''
                      ? 'https://d2r-member.com/image/d2rs' + selectedItem.id + '.png'
                      : 'https://d2r-member.com/image/' + selectedItem.image_path
                    : 'https://d2r-member.com/image/' + selectedItem.product_image_path
                }
                style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
                alt={selectedItem.title || selectedItem.name}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={12}>
              {sessionToken && selectedItem.context !== 'all' ? (
                <div>
                  <h2>{t('search.vendor.advancedOptions')}</h2>
                  <h4>{selectedItem.status}</h4>
                  <br />
                  {selectedItem.status === 'in_cart' && (
                    <GridItem xs={12} sm={12} md={12}>
                      <Button
                        color="primary"
                        style={{ width: '100%' }}
                        onClick={() => cartItem(event, selectedItem.id)}
                      >
                        {t('search.cart.takeFromCart')}
                      </Button>
                    </GridItem>
                  )}
                  {selectedItem.status === 'available' && (
                    <GridItem xs={12} sm={12} md={12}>
                      <Button
                        color="primary"
                        style={{ width: '100%' }}
                        onClick={() => cartItem(event, selectedItem.id)}
                      >
                        {t('search.cart.addToCart')}
                      </Button>
                    </GridItem>
                  )}
                </div>
              ) : (
                <div></div>
              )}
            </GridItem>
          </GridContainer>
        </DialogContent>
      </Dialog>

      <BubbleContainer pageKey="publicPage" helpText={t('help.publicPage')} />
    </div>
  );
}
